import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { StyledButton } from 'components/Providers/styles';
import {
  CopiedMessageShortLink,
  StyledTitleShortLink,
} from 'components/Order/OrderConfirmModal/styled';
import { useCopy } from 'hooks/useCopy';
import { convertShortLInk } from 'hooks/useCoupons';
import DeleteIcon from 'assets/icons/delete.svg';
import CheckActive from 'assets/icons/checkbox_active.svg';
import CheckInactive from 'assets/icons/checkbox_inactive.svg';
import { getCouponsPOS, deleteCouponPOS } from 'redux/actions/Pos/coupons';
import {
  getPromotionsPOS,
  createPromotionPOS,
  updatePromotionPOS,
} from 'redux/actions/Pos/promotions';
import { usePosMenuOptions } from 'pages/PosSettings/hooks/usePosMenuOptions';
import {
  PROMOTION_TYPES,
  buildPromotionAction,
  describeAction,
} from 'pages/PosSettings/Promotions/promotionAction';
import { buildCouponPayload } from 'pages/PosSettings/Coupons/couponForm';
import {
  ALL_CHANNEL,
  WEB_CHANNEL,
  buildChannels,
  channelsToFormValue,
} from 'pages/PosSettings/channels';
import { MarketingTypes } from 'components/Coupons/types';

const ACTIVE_STATUS = 'ACTIVE';

const promoUuid = (promotion) =>
  promotion?.uuid || promotion?._uuid || promotion?.promotionDetail?.uuid;

const promoDetail = (promotion) => {
  if (promotion?.promotionDetail) return promotion.promotionDetail;
  return {
    name: promotion?.name || promotion?._name,
    type: promotion?.type || promotion?._type,
    trigger: promotion?.trigger || promotion?._trigger,
    status: promotion?.status || promotion?._status,
    actions: promotion?.actions || promotion?._actions,
    channels: promotion?.channels || promotion?._channels,
  };
};

/** Reads the single order-level action a coupon promotion carries. */
const promoAction = (promotion) => (promoDetail(promotion)?.actions || [])[0];

const toIsoOrUndefined = (value) =>
  value ? new Date(value).toISOString() : undefined;

const typeLabel = (type) =>
  PROMOTION_TYPES.find((option) => option.value === type)?.label || type || '—';

export const usePosCoupons = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { productOptions, categoryOptions } = usePosMenuOptions();
  const { currentBusiness } = useSelector(({ business }) => business);
  const {
    coupons = [],
    promotions = [],
    loading,
  } = useSelector(({ pos }) => pos);
  const { handleCopy, showCopyMessage, copiedRowId } = useCopy();

  const businessUuid = currentBusiness?.uuid;
  const businessId = currentBusiness?._id;

  useEffect(() => {
    if (!businessUuid) return;
    dispatch(getCouponsPOS({ businessUuid }));
    dispatch(getPromotionsPOS(businessUuid));
  }, [businessUuid]);

  const promotionByUuid = useMemo(() => {
    const map = {};
    promotions.forEach((promotion) => {
      const uuid = promoUuid(promotion);
      if (uuid) map[uuid] = promotion;
    });
    return map;
  }, [promotions]);

  const rows = useMemo(
    () =>
      coupons.map((coupon) => {
        const promotion = promotionByUuid[coupon?.promotionUuid];
        const action = promoAction(promotion) || {};
        const promotionType = promoDetail(promotion)?.type || '';
        return {
          id: coupon?.uuid,
          uuid: coupon?.uuid,
          couponCode: coupon?.code,
          couponType: coupon?.type,
          status: coupon?.status,
          promotionUuid: coupon?.promotionUuid,
          promotionName: promoDetail(promotion)?.name || '—',
          promotionType,
          promotionTypeLabel: typeLabel(promotionType),
          discountLabel: describeAction(promotionType, action),
          action,
          channels: promoDetail(promotion)?.channels || [],
          freeDelivery: promotionType === 'FREE_DELIVERY',
          startAt: coupon?.startAt || null,
          endAt: coupon?.endAt || null,
          maxUses: coupon?.limits?.maxUses ?? null,
          maxUsesPerCustomer: coupon?.limits?.maxUsesPerCustomer ?? null,
          maxUsesPerOrder: coupon?.limits?.maxUsesPerOrder ?? null,
          maxUsesPerStore: coupon?.limits?.maxUsesPerStore ?? null,
          maxTotalDiscount: coupon?.limits?.maxTotalDiscount ?? null,
          used:
            (coupon?.counters?.openingUsedCount || 0) +
            (coupon?.counters?.usedCount || 0),
          _raw: coupon,
        };
      }),
    [coupons, promotionByUuid]
  );

  const activeCoupons = useMemo(
    () => rows.filter((row) => row.status === ACTIVE_STATUS),
    [rows]
  );

  const inactiveCoupons = useMemo(
    () => rows.filter((row) => row.status !== ACTIVE_STATUS),
    [rows]
  );

  /**
   * The coupons marketing campaigns can hand out: active, marked for marketing
   * when they were saved, and not past their end date. A coupon without an end
   * date does not expire.
   */
  const marketingCoupons = useMemo(
    () =>
      activeCoupons.filter(
        (row) =>
          row._raw?.legacyType === MarketingTypes.MARKETING &&
          (!row.endAt || moment(row.endAt).isSameOrAfter(moment()))
      ),
    [activeCoupons]
  );

  const buildColumns = (handleDelete, handleEdit) => [
    {
      field: 'couponCode',
      headerName: t('website_settings.coupon_code'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'promotionName',
      headerName: t('Promotion'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'promotionTypeLabel',
      headerName: t('Type'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'discountLabel',
      headerName: t('website_settings.amount_percent'),
      flex: 1.2,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ value }) => (
        <Typography sx={{ textAlign: 'right', width: '100%' }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'startAt',
      headerName: t('website_settings.start_date_'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ value }) =>
        value ? moment(value).format('MM/DD/YYYY') : '—',
    },
    {
      field: 'endAt',
      headerName: t('website_settings.end_date_'),
      flex: 1,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ value }) =>
        value ? moment(value).format('MM/DD/YYYY') : '—',
    },
    {
      field: 'used',
      headerName: t('Used'),
      flex: 0.6,
      sortable: false,
      filterable: false,
      editable: false,
    },
    {
      field: 'maxUses',
      headerName: t('website_settings.max_use'),
      flex: 0.6,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ value }) => (value === null ? '∞' : value),
    },
    {
      field: 'shortLink',
      headerName: t('Website link'),
      editable: false,
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const channels = channelsToFormValue(row?.channels);
        if (!channels.includes(ALL_CHANNEL) && !channels.includes(WEB_CHANNEL)) {
          return '—';
        }
        const baseUrl = currentBusiness?.settings?.baseUrl || '';
        const alias = currentBusiness?.settings?.url || '';
        const currentUrl = convertShortLInk(baseUrl, alias, row?.couponCode);
        return (
          <Box sx={{ display: 'flex', gap: '2px', width: '100%', position: 'relative' }}>
            <Tooltip title={currentUrl} placement="top">
              <StyledTitleShortLink>{currentUrl}</StyledTitleShortLink>
            </Tooltip>
            <StyledButton
              sx={{ width: '20px' }}
              loading={false}
              variant="text"
              startIcon={
                <ContentCopyOutlinedIcon
                  style={{ color: '#929398', width: '24px', height: '24px' }}
                />
              }
              onClick={() => handleCopy(currentUrl, row?.id)}
            />
            {showCopyMessage && row?.id === copiedRowId && (
              <CopiedMessageShortLink className="message-container show fade-in">
                <Typography color="white" variant="body2" className="message-text">
                  {t('Copied')}
                </Typography>
              </CopiedMessageShortLink>
            )}
          </Box>
        );
      },
    },
    {
      field: 'freeDelivery',
      headerName: t('website_settings.free_delivery'),
      flex: 0.8,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: ({ value }) => (
        <img
          src={value ? CheckActive : CheckInactive}
          alt={value ? 'active' : 'inactive'}
        />
      ),
    },
    {
      field: 'Tools',
      headerName: t('settings.tools'),
      type: 'actions',
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex' }}>
          <IconButton aria-label="edit" onClick={() => handleEdit(params?.row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => handleDelete(params?.row)}
          >
            <img src={DeleteIcon} alt="delete icon" />
          </IconButton>
        </Box>
      ),
    },
  ];

  /**
   * A coupon owns exactly one promotion, so both are written in a single call:
   * the service creates or updates them together and leaves neither behind when
   * the other is rejected.
   */
  const buildBody = (values, startDate, endDate) => ({
    businessUuid,
    name: values.promotionName,
    type: values.type,
    trigger: 'CODE',
    channels: buildChannels(values.channels),
    status: values.active ? 'ACTIVE' : 'INACTIVE',
    startAt: toIsoOrUndefined(startDate),
    endAt: toIsoOrUndefined(endDate),
    conditions: [],
    actions: [buildPromotionAction(values)],
    coupon: buildCouponPayload({
      values,
      startAt: toIsoOrUndefined(startDate),
      endAt: toIsoOrUndefined(endDate),
      active: values.active,
      businessId,
    }),
  });

  const saveCoupon = async ({ values, startDate, endDate, editing }) => {
    const body = buildBody(values, startDate, endDate);

    if (editing?.uuid) {
      await dispatch(updatePromotionPOS({ id: editing.promotionUuid, body }));
    } else {
      await dispatch(createPromotionPOS(body));
    }

    // One read once everything is written, rather than one per request.
    return Promise.all([
      dispatch(getCouponsPOS({ businessUuid })),
      dispatch(getPromotionsPOS(businessUuid)),
    ]);
  };

  const removeCoupon = (row) => dispatch(deleteCouponPOS(row?.uuid));

  return {
    loading,
    activeCoupons,
    inactiveCoupons,
    marketingCoupons,
    productOptions,
    categoryOptions,
    buildColumns,
    saveCoupon,
    removeCoupon,
  };
};
